(() => {
  if (customElements.get('eb-range-explorer')) return;

  customElements.define('eb-range-explorer', class extends HTMLElement {
    connectedCallback() {
      const config = this.querySelector('[data-range-explorer-config]');
      if (!config || !window.EBRangeCore) return;

      this.config = JSON.parse(config.textContent);
      this.models = this.config.models || [];
      this.unit = this.dataset.unit || this.config.unit || 'km';
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.modelIndex = 0;
      this.terrain = this.config.defaultTerrain || 'mixed';
      this.weight = Number(this.config.defaultWeight || 75);
      this.mode = '';
      this.env = {};

      this.low = this.querySelector('[data-range-low]');
      this.high = this.querySelector('[data-range-high]');
      this.mid = this.querySelector('[data-range-mid]');
      this.arc = this.querySelector('[data-range-arc]');
      this.summary = this.querySelector('[data-range-summary]');
      this.compare = this.querySelector('[data-range-compare]');
      this.weightOut = this.querySelector('[data-range-weight-out]');
      this.weightInput = this.querySelector('[data-range-weight]');
      this.speed = this.querySelector('[data-range-speed]');
      this.charge = this.querySelector('[data-range-charge]');
      this.eff = this.querySelector('[data-range-eff]');
      this.wear = this.querySelector('[data-range-wear]');
      this.modeGroup = this.querySelector('[data-range-mode-group]');
      this.modeOptions = this.querySelector('[data-range-mode-options]');

      this.bind();
      this.renderModes();
      this.update(false);
    }

    bind() {
      this.querySelectorAll('[data-range-model-index]').forEach((button) => {
        button.addEventListener('click', () => {
          this.modelIndex = Number(button.dataset.rangeModelIndex || 0);
          this.mode = '';
          this.renderModes();
          this.update(true);
        });
      });

      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-range-terrain]')) this.terrain = event.target.value;
        if (event.target.matches('[data-range-mode]')) this.mode = event.target.value;
        if (event.target.matches('[data-range-env]')) {
          const factor = Number(event.target.dataset.rangeEnv || 1);
          if (event.target.checked) this.env[event.target.value] = factor;
          else delete this.env[event.target.value];
          const wrap = event.target.closest('[data-range-env-row]');
          if (wrap) wrap.classList.toggle('is-active', event.target.checked);
        }
        this.update(true);
      });

      this.weightInput?.addEventListener('input', (event) => {
        this.weight = Number(event.target.value);
        if (this.weightOut) this.weightOut.textContent = String(this.weight);
        this.update(true);
      });

      this.compare?.addEventListener('click', () => this.addToCompare());
    }

    currentModel() {
      return this.models[this.modelIndex] || this.models[0] || {};
    }

    renderModes() {
      const model = this.currentModel();
      const modes = Array.isArray(model.modes) ? model.modes.filter((mode) => mode.name) : [];
      if (!this.modeGroup || !this.modeOptions) return;

      this.modeGroup.hidden = modes.length === 0;
      this.modeOptions.innerHTML = modes.map((mode, index) => {
        const checked = index === 0;
        if (checked) this.mode = mode.id;
        return `
          <label class="eb-range-explorer__option eb-range-explorer__option--mode" for="range-exp-${this.dataset.sectionId}-mode-${this.escape(mode.id)}">
            <input id="range-exp-${this.dataset.sectionId}-mode-${this.escape(mode.id)}" type="radio" name="range-exp-mode-${this.dataset.sectionId}" value="${this.escape(mode.id)}" data-range-mode data-range-label="${this.escape(mode.name)}" ${checked ? 'checked' : ''}>
            <span><b>${this.escape(mode.name)}</b></span>
          </label>
        `;
      }).join('');
    }

    update(animate) {
      const model = this.currentModel();
      if (!model) return;

      this.querySelectorAll('[data-range-model-index]').forEach((button) => {
        const active = Number(button.dataset.rangeModelIndex || 0) === this.modelIndex;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });

      const core = window.EBRangeCore;
      const base = core.modeBase(model, this.mode);
      const envFactors = Object.values(this.env);
      const factors = [
        core.terrain[this.terrain] ?? 1,
        core.weightFactor(this.weight),
        ...envFactors,
      ];
      const range = core.estimate(base, factors);
      const fill = core.fill(range, model.max || base);

      this.writeNumber(this.low, this.displayValue(range.low), animate);
      this.writeNumber(this.high, this.displayValue(range.high), animate);
      this.writeNumber(this.mid, this.displayValue(range.high), animate);
      this.arc?.style.setProperty('--range-meter', String(fill));
      this.setAttribute('data-range-tone', core.tone(fill));

      const topSpeed = core.topSpeed(model, this.mode);
      const efficiency = core.efficiency(model.battery, range.mid);
      const wear = core.batteryWear(model, this.mode, envFactors);
      if (this.speed) this.speed.textContent = topSpeed ? Math.round(topSpeed) : '—';
      if (this.charge) this.charge.textContent = model.chargeTime ? String(model.chargeTime) : '—';
      if (this.eff) this.eff.textContent = efficiency ? efficiency.toFixed(1) : '—';
      if (this.wear) this.wear.textContent = wear ? `${wear}%` : '—';

      this.updateSummary();
    }

    displayValue(value) {
      if (this.unit === 'mi') return Math.round(Number(value || 0) * 0.621371);
      return Number(value || 0);
    }

    writeNumber(node, value, animate) {
      if (!node) return;
      const from = Number(node.textContent || value);
      const to = Number(value || 0);
      if (!animate || this.reducedMotion.matches || from === to) {
        node.textContent = String(to);
        return;
      }
      const start = performance.now();
      const duration = 700;
      const tick = (now) => {
        const progress = Math.min((now - start) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        node.textContent = String(Math.round(from + (to - from) * eased));
        if (progress < 1) requestAnimationFrame(tick);
        else node.textContent = String(to);
      };
      requestAnimationFrame(tick);
    }

    updateSummary() {
      if (!this.summary) return;
      const terrain = this.querySelector('[data-range-terrain]:checked')?.dataset.rangeLabel || '';
      const mode = this.querySelector('[data-range-mode]:checked')?.dataset.rangeLabel || '';
      const weightText = `${this.weight}kg`;
      this.summary.textContent = [terrain, weightText, mode].filter(Boolean).join(' · ');
    }

    addToCompare() {
      const model = this.currentModel();
      if (!model?.id) return;

      if (this.dataset.comparePage) {
        const url = new URL(this.dataset.comparePage, window.location.origin);
        const handles = this.models.map((m) => m.handle).filter(Boolean).slice(0, 4);
        if (handles.length) url.searchParams.set('products', handles.join(','));
        window.location.href = url.toString();
        return;
      }

      let items = [];
      try {
        items = JSON.parse(sessionStorage.getItem('eb:compare') || '[]');
      } catch (error) {
        items = [];
      }
      items = items.filter((item) => String(item.id) !== String(model.id));
      items.push({ id: String(model.id), handle: model.handle, title: model.title, url: model.url, image: model.image });
      sessionStorage.setItem('eb:compare', JSON.stringify(items.slice(-4)));
      this.compare?.classList.add('is-added');
    }

    escape(value) {
      return String(value || '').replace(/[&<>"']/g, (char) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
      })[char]);
    }
  });
})();
